import { ActionButton } from "@wine2trace/ui/FormComponents";
import useLabels from "../../hooks/useLabels";
import { useSelector } from "react-redux";
import { selectConfigs, selectUser } from "../../context/selectors";
import { formatTankUnit, resolveTankUnit, tankOccupancy } from "../../services/tankUnit";
import { barPath } from "../Chart/barPath";

// "2026-10-10" is a calendar day: build it in local time so it never shifts by timezone.
const parseDay = (value) => {
  const [year, month, day] = String(value).split("-").map(Number);
  return new Date(year, month - 1, day);
};

// Day names follow the language the user chose (configs.locale, e.g. "it" or "en_GB").
const toIntlLocale = (locale) => (locale ? String(locale).replace("_", "-") : undefined);
const weekdayShort = (value, locale) => parseDay(value).toLocaleDateString(locale, { weekday: "short" });
const dayLong = (value, locale) =>
  parseDay(value).toLocaleDateString(locale, { weekday: "short", day: "numeric", month: "numeric" });

// The occupancy ring (r and its length) and the day bars, in the units of their viewBox.
const RING_RADIUS = 52;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;
const BAR_WIDTH = 24;
const BAR_HEIGHT = 100;

const TankOccupancy = ({ tanks, unit: tankUnit, getLabel }) => {
  if (tanks === null) return <p className="home_status_unavailable">{getLabel("homeStatusSectionUnavailable")}</p>;
  // No tanks (or no capacity) configured: a percentage would be meaningless.
  if (!tanks.total || !tanks.capacity_l) return null;

  const percent = tankOccupancy(tanks.quantity_l, tanks.capacity_l);
  const { displayedQuantity, displayedCapacity, unit } = formatTankUnit(tanks.quantity_l, tanks.capacity_l, tankUnit);
  const filled = Math.min(tanks.quantity_l, tanks.capacity_l) / tanks.capacity_l;

  return (
    <div className="home_status_block home_status_tanks">
      <div className="home_status_donut" aria-hidden="true">
        {/* The ring: the free part under, the filled part from the top, clockwise. */}
        <svg viewBox="0 0 120 120" className="home_status_ring">
          <circle className="home_status_slice_free" cx="60" cy="60" r={RING_RADIUS} />
          <circle
            className="home_status_slice_filled"
            cx="60"
            cy="60"
            r={RING_RADIUS}
            strokeDasharray={`${filled * RING_LENGTH} ${RING_LENGTH}`}
            transform="rotate(-90 60 60)"
          />
        </svg>
        <span className="home_status_donut_value">{percent}%</span>
      </div>
      <ul className="home_status_facts">
        <li className="home_status_fact_main">
          {getLabel("homeTanksVolume", { quantity: displayedQuantity, capacity: displayedCapacity, unit, percent })}
        </li>
        <li>{getLabel("homeTanksOccupied", { occupied: tanks.occupied, total: tanks.total })}</li>
      </ul>
    </div>
  );
};

const CompletedWeek = ({ operations, locale, getLabel }) => {
  if (operations === null) return <p className="home_status_unavailable">{getLabel("homeStatusSectionUnavailable")}</p>;
  const week = operations.completed_last_7d || [];
  if (week.length === 0) return null;

  const total = week.reduce((sum, day) => sum + day.count, 0);
  const top = Math.max(...week.map((day) => day.count));
  const data = week.map((day) => ({ ...day, label: weekdayShort(day.date, locale) }));
  const title = getLabel("homeCompletedWeekTitle");

  return (
    <div className="home_status_block home_status_week">
      <h5 className="home_status_subtitle">{title}</h5>
      <p className="home_status_total">{getLabel("homeCompletedWeekTotal", { count: total })}</p>
      {/* A bar per day from the baseline, the count written on top; the busiest day is the full height. */}
      <ol className="home_status_bars" aria-hidden="true">
        {data.map((day) => {
          const height = top ? (day.count / top) * BAR_HEIGHT : 0;
          return (
            <li key={day.date} className="home_status_day">
              <span className="home_status_day_count">{day.count}</span>
              <svg className="home_status_day_bar" viewBox={`0 0 ${BAR_WIDTH} ${BAR_HEIGHT}`}>
                <path className="home_status_bar" d={barPath(height, BAR_WIDTH, BAR_HEIGHT)}>
                  <title>{`${dayLong(day.date, locale)}: ${day.count}`}</title>
                </path>
              </svg>
              <span className="home_status_day_label">{day.label}</span>
            </li>
          );
        })}
      </ol>
      {/* The drawing is decorative: the same numbers, as a table. */}
      <table className="home_visually_hidden" aria-label={title}>
        <thead>
          <tr>
            <th scope="col">{getLabel("homeCompletedWeekDay")}</th>
            <th scope="col">{getLabel("homeCompletedWeekCount")}</th>
          </tr>
        </thead>
        <tbody>
          {week.map((day) => (
            <tr key={day.date}>
              <th scope="row">{dayLong(day.date, locale)}</th>
              <td>{day.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// "Stato cantina": what the cellar looks like now (tank fill) and how busy the last week was.
// Which parts exist is decided by the server: a section the user cannot read is absent.
const HomeCellarStatus = ({ summary, status, onRetry }) => {
  const [getLabel] = useLabels();
  const user = useSelector(selectUser);
  const configs = useSelector(selectConfigs);

  if (status === "idle") return null;

  const title = getLabel("homeCellarStatusTitle");
  const tankUnit = resolveTankUnit(user?.settings?.tank_unit);
  const locale = toIntlLocale(configs?.locale);

  if (status === "loading") {
    return (
      <section className="home_panel" aria-label={title}>
        <div data-testid="home-status-loading" aria-busy="true" className="home_panel_placeholder" />
      </section>
    );
  }

  if (status === "error") {
    return (
      <section className="home_panel" aria-label={title}>
        <h4 className="home_panel_title">{title}</h4>
        <div className="home_panel_error">
          <p>{getLabel("homeSummaryError")}</p>
          <ActionButton icon="fa-rotate" onClick={onRetry}>
            {getLabel("homeSummaryRetry")}
          </ActionButton>
        </div>
      </section>
    );
  }

  const hasTanks = summary?.tanks !== undefined;
  const hasOperations = summary?.operations !== undefined;
  if (!hasTanks && !hasOperations) return null;

  return (
    <section className="home_panel home_status" aria-label={title}>
      <h4 className="home_panel_title">{title}</h4>
      <div className="home_status_content">
        {hasTanks && <TankOccupancy tanks={summary.tanks} unit={tankUnit} getLabel={getLabel} />}
        {hasOperations && <CompletedWeek operations={summary.operations} locale={locale} getLabel={getLabel} />}
      </div>
    </section>
  );
};

export default HomeCellarStatus;
